import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { Redirect, useLocation } from 'react-router-dom';

import EventOnlineView from '../components/content/event/EventOnlineView';
import Faq from '../components/content/Faq';
import InsufficientResultsInformation from '../components/content/informations/InsufficientResultsInformation';
import SuggestionsMediaList from '../components/content/lists/SuggestionsMediaList';
import EventCommonPart from '../components/content/event/EventCommonPart';
import EventSearchMainActions from '../components/content/event/EventSearchMainActions';

import { useWindowSize } from '../hooks';
import { getMainSearchResultUrl } from '../utils/common';
import { includesFotoFlat } from '../utils/common';
import { UserContext } from '../components/context/UserContext';
import { MAIN_SEARCH_BIB, MAIN_SEARCH_COMBINED, MAIN_SEARCH_FACE } from '../utils/variables';
import { customScrollToComponent } from '../utils/common';

////////////////////////////////////////////
// TODO MISSING CASES:
// useEffect
////////////////////////////////////////////

const EventSmartSearchPage = (props) => {
  const { event } = props.viewer;
  const { customerKey, viewer, snFromUrl, history, codeSecondFactorForFaceEvent } = props;
  const { participant } = event;

  const [altSearchPosition, setAltSearchPosition] = useState(null);
  const [formRefPosition, setFormRefPosition] = useState(null);
  const [shouldShowMainSearch, setShouldShowMainSearch] = useState(false);
  const [elementToAnimate, setElementToAnimate] = useState(null);
  const [searchTypeToForceShow, setSearchTypeToForceShow] = useState(null);

  const suggestionsCount = participant ? participant.suggestions.mediaInfo.count : 0;
  const participantMediaCount = event.participant
    ? event.participant.media.mediaInfo.count
    : 0;
  const eventHasSecondFactor = Boolean(event.secondFactorType?.type);

  const { onMarathonFotoChange } = useContext(UserContext);

  const { search: searchQueryFromUrl } = useLocation();

  const { windowWidth } = useWindowSize();

  const eventOnlineView = useRef();
  const mediaSection = useRef();

  useEffect(() => {
    if (onMarathonFotoChange) {
      onMarathonFotoChange(event.marathonFoto);
    }
    scrollToMediaSection();
  }, []);

  useEffect(() => {
    if (searchTypeToForceShow !== null) {
      const timeout = setTimeout(() => setSearchTypeToForceShow(null), 2000);
      return () => clearTimeout(timeout);
    }
  }, [searchTypeToForceShow]);

  useEffect(() => {
    if (shouldShowMainSearch) {
      const timeout = setTimeout(() => setSearchTypeToForceShow(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [shouldShowMainSearch]);

  const liftingUpAltSearchPositionData = (altSerachPositionRef) => {
    setAltSearchPosition(altSerachPositionRef);
  };
  const liftingUpFormPositionData = (formPosition) => {
    setFormRefPosition(formPosition);
  };

  const scrollToAltSearch = () => {
    if (altSearchPosition && altSearchPosition.current) {
      customScrollToComponent({
        currentRef: altSearchPosition.current,
        shouldPassSTickyDesktopBtns: true,
        windowWidth: windowWidth,
      });
    }
  };

  const scrollToMediaSection = () => {
    if (mediaSection && mediaSection.current) {
      customScrollToComponent({
        currentRef: mediaSection.current,
        timeout: 400,
        shouldPassSTickyDesktopBtns: true,
        windowWidth: windowWidth,
      });
    }
  };

  const scrollToForm = () => {
    if (formRefPosition && formRefPosition.current) {
      customScrollToComponent({
        currentRef: formRefPosition.current,
        shouldPassSTickyDesktopBtns: true,
        windowWidth: windowWidth,
      });
    }
  };

  const onScrollToMainSearch = () => {
    setShouldShowMainSearch(true);
    scrollToForm();
    setTimeout(() => {
      setShouldShowMainSearch(false);
    }, 100);
  };

  const setCurrentElementToAnimate = (element) => {
    setElementToAnimate(element);
  };

  const goBackToMainSearchResults = () => {
    const destinationUrl = getMainSearchResultUrl(
      event.sgId,
      snFromUrl,
      eventHasSecondFactor,
      customerKey,
      searchQueryFromUrl,
    );
    history.push(destinationUrl);
  };

   const scrollToExtendedSearch = (searchType) => {
     scrollToForm();
     searchType && setSearchTypeToForceShow(searchType);   
    };

  if (event.state === 'reminder') {
    return <Redirect to={`/event/${event.sgId}`} />;
  }

  if (!event.smartSearch) {
    return <Redirect to={`/event/${event.sgId}`} />;
  }

  return (
    <div className="container-960 container-page">
      <EventCommonPart event={viewer.event}>
        <EventSearchMainActions
          onFindMorePhotosClick={scrollToAltSearch}
          event={viewer.event}
          onGoBackToMainSearch={goBackToMainSearchResults}
          onScrollToForm={onScrollToMainSearch}
          participantMediaCount={participantMediaCount}
          setElementToAnimate={setCurrentElementToAnimate}
          hasEventCombinedSearch={event.mainSearch === MAIN_SEARCH_COMBINED}
        />
        <div ref={eventOnlineView}>
          <EventOnlineView
            customerKey={customerKey}
            onlineEvent={event}
            urlSn={snFromUrl}
            hasSmartSearchTab={event.smartSearch && participantMediaCount !== 0}
            hasGpsSearchTab={event.gps}
            hasDualSearchTab={event.searchDual}
            codeSecondFactorForFaceEvent={codeSecondFactorForFaceEvent}
            showStillProcessingHint={event.pendingMediaProcessing}
            goBackToMainSearchResults={goBackToMainSearchResults}
            setAltSearchRef={liftingUpAltSearchPositionData}
            setFormRef={liftingUpFormPositionData}
            shouldShowMainSearch={shouldShowMainSearch}
            elementToAnimate={elementToAnimate}
            isParticipant={Boolean(event.participant)}
            searchTypeToForceShow={searchTypeToForceShow}
          />
        </div>
      </EventCommonPart>
      <div ref={mediaSection}>
        {suggestionsCount !== 0 && (
          <SuggestionsMediaList
            customerKey={customerKey}
            eventHasSearchByFace={event.mainSearch === MAIN_SEARCH_FACE}
            eventHasSecondFactor={event.secondFactorType ? true : false}
            eventId={event.sgId}
            startnumber={event.participant.startnumber}
            suggestionsMedia={event.participant.suggestions}
            offer={event}
            participantMediaCount={participantMediaCount}
            goBackToMainSearchResults={goBackToMainSearchResults}
            eventHasFotoFlat={includesFotoFlat(event.products)}
          />
        )}
        {suggestionsCount === 0 && (
          <InsufficientResultsInformation
            count={participantMediaCount}
            raceResultsUrl={event.hostResultUrl}
            showGpsSearchLink={event.gps}
            showDualSearchLink={event.searchDual}
            showSmartSearchLink={event.smartSearch}
            tagWithStartNumber={event.mainSearch === MAIN_SEARCH_BIB}
            isInSmartSearch={true}
            isCombinedSearchEvent={event.mainSearch === MAIN_SEARCH_COMBINED}
            showExtendedSearchAsAvailable={true}
            scrollToMainSearchForm={() => {
              scrollToForm();
              setShouldShowMainSearch(true);
            }}
            onSearchOptionBtnClick={scrollToExtendedSearch}
          />
        )}
      </div>
      <Faq onFindMore={() => scrollToAltSearch()} hasExtendedOptions={true} />
    </div>
  );
};

EventSmartSearchPage.propTypes = {
  customerKey: PropTypes.string,
  snFromUrl: PropTypes.string,
  viewer: PropTypes.object,
  codeSecondFactorForFaceEvent: PropTypes.string,
  history: PropTypes.object,
};

export { EventSmartSearchPage };

export default createFragmentContainer(EventSmartSearchPage, {
  viewer: graphql`
    fragment EventSmartSearchPage_viewer on Viewer {
      event(id: $eventId) {
        id
        sgId
        gps
        hostResultUrl
        products {
          type
        }
        pendingMediaProcessing
        participant(startnumber: $startnumber, secondFactor: $secondFactor) {
          media {
            mediaInfo {
              count
            }
          }
          suggestions {
            mediaInfo {
              count
            }
            ...SuggestionsMediaList_suggestionsMedia
          }
          startnumber
        }
        secondFactorType {
          type
        }
        mainSearch
        searchDual
        smartSearch
        state
        marathonFoto
        ...EventOnlineView_onlineEvent
        ...SuggestionsMediaList_offer
        ...EventCommonPart_event
        ...EventSearchMainActions_event
      }
    }
  `,
});
